import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { Box, FormHelperText, FormControl, TextField, Typography, FormLabel, IconButton } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { CalendarIcon } from "../../../assets/icons/go-hire-icons";
import { CreateContractData } from "../../../types/service.type";
import { ServiceRequestTypeEnum } from "../../../enum/service.enum";

const MilestoneField = ({ index, serviceType }: { index: number; serviceType?: ServiceRequestTypeEnum }) => {
  const { t } = useTranslation("goService", { keyPrefix: "createContractPage" });
  const {
    control,
    formState: { errors },
  } = useFormContext<CreateContractData>();

  const milestoneErrors = errors.milestones?.[index];

  return (
    <Box sx={styles.container}>
      <Typography sx={{ ...styles.milestoneTitle, pt: "16px" }}>{t("milestone", { number: index + 1 })}</Typography>
      <FormControl fullWidth component="fieldset" sx={styles.formControl} error={!!milestoneErrors?.name}>
        <FormLabel sx={styles.formLabel}>{t("milestoneName")}</FormLabel>
        <Controller
          name={`milestones.${index}.name`}
          control={control}
          render={({ field }) => (
            <TextField
              {...field}
              fullWidth
              sx={styles.textField}
              placeholder={t("enterNameOfMilestone")}
              error={!!milestoneErrors?.name}
            />
          )}
        />
        {milestoneErrors?.name && <FormHelperText error>{t(milestoneErrors.name.message)}</FormHelperText>}
      </FormControl>

      <FormControl fullWidth component="fieldset" sx={styles.formControl} error={!!milestoneErrors?.deliveryDueDate}>
        <FormLabel sx={styles.formLabel}>{t("serviceDeliveryDateAndTime")}</FormLabel>
        <Controller
          name={`milestones.${index}.deliveryDueDate`}
          control={control}
          render={({ field }) => (
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DateTimePicker
                {...field}
                value={field.value ? new Date(field.value) : null}
                onChange={(date) => field.onChange(date?.toISOString())}
                views={["year", "month", "day", "hours", "minutes"]}
                disablePast
                slotProps={{
                  textField: {
                    error: !!milestoneErrors?.deliveryDueDate,
                    helperText: milestoneErrors?.deliveryDueDate?.message && t(milestoneErrors.deliveryDueDate.message),
                    sx: styles.textField,
                  },
                }}
                slots={{
                  openPickerIcon: CalendarIcon,
                }}
              />
            </LocalizationProvider>
          )}
        />
      </FormControl>
      {serviceType === ServiceRequestTypeEnum.Unit && (
        <FormControl
          fullWidth
          component="fieldset"
          sx={{ ...styles.formControl, pb: "16px" }}
          error={!!milestoneErrors?.paymentDueDate}
        >
          <FormLabel sx={styles.formLabel}>{t("milestonePaymentDateAndTime")}</FormLabel>
          <Controller
            name={`milestones.${index}.paymentDueDate`}
            control={control}
            render={({ field }) => (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DateTimePicker
                  {...field}
                  value={field.value ? new Date(field.value) : null}
                  onChange={(date) => field.onChange(date?.toISOString())}
                  views={["year", "month", "day", "hours", "minutes"]}
                  disablePast
                  slotProps={{
                    textField: {
                      error: !!milestoneErrors?.paymentDueDate,
                      helperText: milestoneErrors?.paymentDueDate?.message && t(milestoneErrors.paymentDueDate.message),
                      sx: styles.textField,
                    },
                  }}
                  slots={{
                    openPickerIcon: CalendarIcon,
                  }}
                />
              </LocalizationProvider>
            )}
          />
        </FormControl>
      )}
    </Box>
  );
};

export default MilestoneField;

const styles = {
  container: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "24px",
    borderRadius: "12px",
    border: "1px solid #EAECF0",
    background: "var(--Base-White, #FFF)",
  },
  headerContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  milestoneTitle: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "18px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "28px",
    px: "16px",
  },
  formControl: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    px: "16px",
  },
  formLabel: {
    fontFamily: "Montserrat",
    color: "var(--Dark---1000, #111)",
    fontSize: "14px",
    fontStyle: "normal",
    fontWeight: 500,
    lineHeight: "20px",
  },
  textField: {
    "& .MuiInputBase-root.MuiOutlinedInput-root": {
      borderRadius: "8px",
      border: "none",
      background: "var(--Gray---50, #F5F6F5)",
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  removeButton: {
    mr: 2,
    color: "error.main",
    fontSize: "20px",
  },
};
